import {
  sendAuthCredential,
  sendAuthEmailVerifyChange,
  sendAuthEmployeeIdLogin,
  sendAuthIdCardLogin,
  sendAuthPasswordLogin,
} from '@/api/auth'
import { ingestSlauthSession } from '@/auth/slauth'
import { useUserStore } from '@/store/user'
import { setLocale } from '@/utils/common'
import type {
  AuthCredentialReqVO,
  AuthEmailVerifyChangeReqVO,
  AuthEmployeeIdLoginReqVO,
  AuthIdCardLoginReqVO,
  AuthPasswordLoginReqVO,
} from '@/types/vo/auth.vo'

type AuthLikeResponse = {
  auth?: string
  token?: string
  user?: any
  tenants?: any[]
  session?: any
}

function normalizeSession(response: AuthLikeResponse) {
  if (response?.auth !== 'slauth') {
    return undefined
  }
  if (!response?.session?.access_token) {
    if (!response?.token) return undefined
    return {
      access_token: response.token,
      token_type: 'Bearer',
      user: response.user,
    }
  }

  const session = {
    ...response.session,
    access_token: response.session.access_token || response.token,
    token_type: response.session.token_type || 'Bearer',
    user: response.user,
  }
  return session
}

export async function commitAuthResponse<T extends AuthLikeResponse>(response: T): Promise<T> {
  if (!response) return response

  const normalizedSession = normalizeSession(response)
  const store = useUserStore()

  if (response.auth === 'slauth' && normalizedSession?.access_token) {
    await ingestSlauthSession(normalizedSession)
  }

  await store.login({
    auth: response.auth || 'slauth',
    user: response.user || {},
    tenants: response.tenants || [],
  })
  if (response?.user?.locale) {
    await setLocale(response.user.locale)
  }

  return {
    ...response,
    session: normalizedSession,
  }
}

export async function loginWithPassword(data: AuthPasswordLoginReqVO) {
  return commitAuthResponse(await sendAuthPasswordLogin(data))
}

export async function loginWithCredential(data: AuthCredentialReqVO) {
  return commitAuthResponse(await sendAuthCredential(data))
}

export async function loginWithEmployeeId(data: AuthEmployeeIdLoginReqVO) {
  return commitAuthResponse(await sendAuthEmployeeIdLogin(data))
}

export async function loginWithIdCard(data: AuthIdCardLoginReqVO) {
  return commitAuthResponse(await sendAuthIdCardLogin(data))
}

export async function loginWithEmailVerifyChange(data: AuthEmailVerifyChangeReqVO) {
  const response = await sendAuthEmailVerifyChange(data)
  if (response?.token || response?.session?.access_token) {
    return commitAuthResponse(response)
  }
  return response
}
